import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Package, Save } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RegionalInventoryTab — RM stock tracker for PPE, cans, bags, etc.
 * Wired to GET/PUT /api/ops/regional-inventory.
 */
export function RegionalInventoryTab() {
  const [items, setItems] = useState([]);
  const [updated_at, setUpdatedAt] = useState(null);
  const [drafts, setDrafts] = useState({}); // sku → count string
  const [savingSku, setSavingSku] = useState('');
  const [newSku, setNewSku] = useState({ sku: '', label: '', count: '' });

  const load = async () => {
    const res = await fetch(`${BACKEND_URL}/api/ops/regional-inventory`, { credentials: 'include' });
    if (res.ok) {
      const d = await res.json();
      setItems(d.items || []);
      setUpdatedAt(d.updated_at || null);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (sku, label, count) => {
    setSavingSku(sku);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ops/regional-inventory/${encodeURIComponent(sku)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: parseInt(count || '0', 10), label: label || undefined }),
      });
      if (!res.ok) throw new Error('save failed');
      toast.success(`${label || sku} updated.`);
      await load();
      setDrafts((d) => { const n = { ...d }; delete n[sku]; return n; });
    } catch (e) {
      toast.error('Could not update inventory.');
    } finally {
      setSavingSku('');
    }
  };

  const addNew = async () => {
    if (!newSku.sku.trim()) {
      toast.error('SKU is required.');
      return;
    }
    await save(newSku.sku.trim(), newSku.label.trim(), newSku.count);
    setNewSku({ sku: '', label: '', count: '' });
  };

  return (
    <Card
      className="bg-zinc-900 border-2 p-4 sm:p-6"
      style={{ borderColor: 'rgba(201, 169, 98, 0.5)' }}
      data-testid="regional-inventory-card"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-bold text-lg flex items-center text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Package className="h-5 w-5 mr-2" style={{ color: '#C9A962' }} />
          OPERATIONS ASSETS · REGIONAL INVENTORY
        </h3>
        {updated_at && (
          <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">
            Updated {new Date(updated_at).toLocaleString()}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {items.map((it) => {
          const draft = drafts[it.sku] ?? String(it.count ?? 0);
          const dirty = String(draft) !== String(it.count ?? 0);
          return (
            <div
              key={it.sku}
              className="flex items-center gap-2 p-2 rounded-md border border-zinc-700 bg-zinc-800/60"
              data-testid={`inventory-row-${it.sku}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold truncate">{it.label || it.sku}</p>
                <p className="text-[10px] text-zinc-400 truncate">{it.sku}</p>
              </div>
              <Input
                type="number"
                min="0"
                value={draft}
                onChange={(e) => setDrafts((d) => ({ ...d, [it.sku]: e.target.value }))}
                className="w-24 h-8 bg-zinc-900 border-zinc-700 text-white text-sm"
                data-testid={`inventory-input-${it.sku}`}
              />
              <Button
                size="sm"
                disabled={!dirty || savingSku === it.sku}
                onClick={() => save(it.sku, it.label, draft)}
                className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-semibold text-xs"
                data-testid={`inventory-save-${it.sku}`}
              >
                {savingSku === it.sku ? '…' : 'Save'}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-zinc-700">
        <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Add a new SKU</p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Input
            placeholder="sku (eg. cones)"
            value={newSku.sku}
            onChange={(e) => setNewSku((s) => ({ ...s, sku: e.target.value }))}
            className="bg-zinc-800 border-zinc-700 text-white text-sm"
            data-testid="inventory-new-sku"
          />
          <Input
            placeholder="Label"
            value={newSku.label}
            onChange={(e) => setNewSku((s) => ({ ...s, label: e.target.value }))}
            className="bg-zinc-800 border-zinc-700 text-white text-sm"
            data-testid="inventory-new-label"
          />
          <Input
            type="number"
            min="0"
            placeholder="Count"
            value={newSku.count}
            onChange={(e) => setNewSku((s) => ({ ...s, count: e.target.value }))}
            className="bg-zinc-800 border-zinc-700 text-white text-sm"
            data-testid="inventory-new-count"
          />
          <Button
            onClick={addNew}
            className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-semibold text-xs"
            data-testid="inventory-new-save"
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            Add SKU
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default RegionalInventoryTab;
